import { TableColumnAlignment } from "atlas-table";

export interface EsgFilter {
    year?: number;
    month?: number;
}

export interface EsgRequestPayload {
    account?: string;
    date?: string;
    country?: string;
}

export interface EsgDataResponse {
    esgData: EsgData[];
    esgDate: any;
}

export interface EsgData {
    carbonFootprint?: CarbonFootprint;
    carbonIntensity?: CarbonIntensity;
    carbonWeight?: CarbonWeight;
    diBoardDiversity?: DiBoardDiversity;
    esg?: Esg;
    modernSlavery?: ModernSlavery;
    sdg?: Sdg;
    votings?: Votings;
    fund?: Fund;
    recordDate?: string;
}

export interface CarbonFootprint {
    footprintFund: number;
    footprintBenchmark: number;
}

export interface CarbonIntensity {
    dgy: number;
    lgy: number;
    gyib: number;
    ib: number;
    gnib: number;
    lgn: number;
    dgn: number;
}

export interface CarbonWeight {
    dgy: number;
    lgy: number;
    gyib: number;
    ib: number;
    gnib: number;
    lgn: number;
    dgn: number;
    notAssessed: number;
}

export interface DiBoardDiversity {
    fund: string;
    benchmark: string;
}

export interface Esg {
    ratingFund: number;
    ratingUniverse: number;
}

export interface ModernSlavery {
    num: number;
    percent: string;
}

export interface Sdg {
    overAllFund: string;
    socialFund: string;
    environmentalFund: string;
    alleviatingPovertyFund: string;
    attainingGenderEqualityFund: string;
    combatingHungerFund: string;
    deliveringEducationFund: string;
    ensuringHealthFund: string;
    providingBasicServicesFund: string;
    safeguardingPeaceFund: string;
    achievingSustainableAgricultureAndForestsFund: string;
    conservingWaterFund: string;
    contributingtoSustainableEnergyUseFund: string;
    mitigatingClimateChangeFund: string;
    optimisingMaterialUseFund: string;
    preservingMarineEcosystemsFund: string;
    preservingTerrestrialEcosystemsFund: string;
    promotingSustainableBuildingsFund: string;
    alleviatingPovertyBenchmark: string;
    attainingGenderEqualityBenchmark: string;
    combatingHungerBenchmark: string;
    deliveringEducationBenchmark: string;
    ensuringHealthBenchmark: string;
    providingBasicServicesBenchmark: string;
    safeguardingPeaceBenchmark: string;
    achievingSustainableAgricultureAndForestsBenchmark: string;
    conservingWaterBenchmark: string;
    contributingToSustainableEnergyUseBenchmark: string;
    mitigatingClimateChangeBenchmark: string;
    optimisingMaterialUseBenchmark: string;
    preservingMarineEcosystemsBenchmark: string;
    preservingTerrestrialEcosystemsBenchmark: string;
    promotingSustainableBuildingsBenchmark: string;
}

export interface Votings {
    meetingsVotedNumAus: number;
    meetingsVotedPercentAus: string;
    meetingsVotedNumGlobalExAus: number;
    meetingsVotedPercentGlobalExAus: string;
    votesAgainstManagementRecPercentGlobal: string;
    supervotesNumGlobal: number;
}

export interface Fund {
    name: string;
}

export interface EsgDiversityAndInclusionTable {
    name: string;
    diBoardDiversityFund: string;
    diBoardDiversityBenchmark: string;
}

export interface EsgModernSlaveryTable {
    name: string;
    companiesRedFlag: string;
    porttfolioPercentRedFlag: string;
}


export const ESG_UN_DEV_GOALS_HEADER = {
    levelThreeSocial: [
        {
            name: 'Name',
            column: 'name',
            displayKey: 'Name',
            fieldKey: 'name',
        },
        {
            name: 'Alleviating Poverty (%)',
            column: 'alleviatingPoverty',
            align: TableColumnAlignment.right,
            displayKey: 'Alleviating Poverty (%)',
            fieldKey: 'alleviatingPoverty',
        },
        {
            name: 'Attaining Gender Equality (%)',
            column: 'attainingGenderEquality',
            align: TableColumnAlignment.right,
            displayKey: 'Attaining Gender Equality (%',
            fieldKey: 'attainingGenderEquality'
        },
        {
            name: 'Combating Hunger (%)',
            column: 'combatingHunger',
            align: TableColumnAlignment.right,
            displayKey: 'Combating Hunger (%)',
            fieldKey: 'combatingHunger'
        },
        {
            name: 'Delivering Education (%)',
            column: 'deliveringEducation',
            align: TableColumnAlignment.right,
            displayKey: 'Delivering Education (%)',
            fieldKey: 'deliveringEducation'
        },
        {
            name: 'Ensuring Health (%)',
            column: 'ensuringHealth',
            align: TableColumnAlignment.right,
            displayKey: 'Ensuring Health (%)',
            fieldKey: 'ensuringHealth'
        },
        {
            name: 'Providing Basic Services (%)',
            column: 'providingBasicServices',
            align: TableColumnAlignment.right,
            displayKey: 'Providing Basic Services (%)',
            fieldKey: 'providingBasicServices'
        },
        {
            name: 'Safeguarding Peace (%)',
            column: 'safeguardingPeace',
            align: TableColumnAlignment.right,
            displayKey: 'Safeguarding Peace (%)',
            fieldKey: 'safeguardingPeace'
        }
    ],
    levelThreeEnvirnomental: [
        {
            name: 'Name',
            column: 'name',
            displayKey: 'Name',
            fieldKey: 'name'
        },
        {
            name: 'Achieving Sustainable Agriculture and Forests (%)',
            column: 'achievingSustainableAgricultureAndForests',
            align: TableColumnAlignment.right,
            displayKey: 'Achieving Sustainable Agriculture and Forests (%)',
            fieldKey: 'achievingSustainableAgricultureAndForests'
        },
        {
            name: 'Conserving Water (%)',
            column: 'conservingWater',
            align: TableColumnAlignment.right,
            displayKey: 'Conserving Water (%)',
            fieldKey: 'conservingWater'
        },
        {
            name: 'Contributing to Sustainable Energy Use (%)',
            column: 'contributingtoSustainableEnergyUse',
            align: TableColumnAlignment.right,
            displayKey: 'Contributing to Sustainable Energy Use (%)',
            fieldKey: 'contributingtoSustainableEnergyUse'
        },
        {
            name: 'Mitigating Climate Change (%)',
            column: 'mitigatingClimateChange',
            align: TableColumnAlignment.right,
            displayKey: 'Mitigating Climate Change (%)',
            fieldKey: 'mitigatingClimateChange'
        },
        {
            name: 'Optimising Material Use (%)',
            column: 'optimisingMaterialUse',
            align: TableColumnAlignment.right,
            displayKey: 'Optimising Material Use (%)',
            fieldKey: 'optimisingMaterialUse'
        },
        {
            name: 'Preserving Marine Ecosystems (%)',
            column: 'preservingMarineEcosystems',
            align: TableColumnAlignment.right,
            displayKey: 'Preserving Marine Ecosystems (%)',
            fieldKey: 'preservingMarineEcosystems'
        },
        {
            name: 'Preserving Terrestrial Ecosystems (%)',
            column: 'preservingTerrestrialEcosystems',
            align: TableColumnAlignment.right,
            displayKey: 'Preserving Terrestrial Ecosystems (%)',
            fieldKey: 'preservingTerrestrialEcosystems'
        },
        {
            name: 'Promoting Sustainable Buildings (%)',
            column: 'promotingSustainableBuildings',
            align: TableColumnAlignment.right,
            displayKey: 'Promoting Sustainable Buildings (%)',
            fieldKey: 'promotingSustainableBuildings'
        }
    ],
    levelTwo: [
        {
            name: 'Name',
            column: 'name',
            displayKey: 'Name',
            fieldKey: 'name'
        },
        {
            name: 'Social (%)',
            column: 'social',
            align: TableColumnAlignment.right,
            displayKey: 'Social (%)',
            fieldKey: 'social'
        },
        {
            name: 'Environmental (%)',
            column: 'environmental',
            align: TableColumnAlignment.right,
            displayKey: 'Environmental (%)',
            fieldKey: 'environmental'
        }
    ],
    levelOne: [
        {
            name: 'Name',
            column: 'name',
            displayKey: 'Name',
            fieldKey: 'name'
        },
        {
            name: 'Overall (%)',
            column: 'overall',
            align: TableColumnAlignment.right,
            displayKey: 'Overall (%)',
            fieldKey: 'overall'
        }
    ]
};



export interface EsgCarbonFootPrintTable {
    name: string;
    secondLastYearCarbonFootPrint: string;
    lastYearCarbonFootPrint: string;
    currentYearCarbonFootPrint: string;
}

export enum EsgSdgLevelTwo {
    social = 'Social',
    environmental = 'Environmental',
}

export enum EsgSdgLevelThreeSocial {
    alleviatingPoverty = 'Alleviating Poverty',
    attainingGenderEquality = 'Attaining Gender Equality',
    combatingHunger = 'Combating Hunger',
    deliveringEducation = 'Delivering Education',
    ensuringHealth = 'Ensuring Health',
    providingBasicServices = 'Providing Basic Services',
    safeguardingPeace = 'Safeguarding Peace'
}

export enum EsgSdgLevelThreeEnvironmental {
    achievingSustainableAgricultureAndForests = 'Achieving Sustainable Agriculture and Forests',
    conservingWater = 'Conserving Water',
    contributingtoSustainableEnergyUse = 'Contributing to Sustainable Energy Use',
    mitigatingClimateChange = 'Mitigating Climate Change',
    optimisingMaterialUse = 'Optimising Material Use',
    preservingMarineEcosystems = 'Preserving Marine Ecosystems',
    preservingTerrestrialEcosystems = 'Preserving Terrestrial Ecosystems',
    promotingSustainableBuildings = 'Promoting Sustainable Buildings'
}

export enum EsgTabs {
    modernSlavery = 'Modern Slavery',
    diversityInclusion = 'Diversity and Inclusion:Gender Diversity on Boards',
    proxyVoting = 'Proxy Voting',
    esgRating = 'ESG Rating Assessment',
    carbonFootprint = 'Carbon Footprint',
    analyticsForClimateTransition = 'Analytics for Climate Transition',
    diversityInclusionTooltip = 'Diversity and Inclusion',
    unDevGoals = 'UN Sustainable Development Goals',
}

export enum EsgTabExportName {
    ModernSlaveryExport = 'ModernSlaveryExport',
    DiversityInclusionExport = 'DiversityInclusionExport',
    ProxyVotingExport = 'ProxyVotingExport',
    EsgRatingExport = 'ESG_Rating_Assessment_Export',
    CarbonFootprintExport = 'CarbonFootprintExport',
    analyticsForClimateTransitionExport = 'AnalyticsForClimateTransitionExport',
    unDevGoals = 'UN_SustainableDevelopmentGoals_Export'
}

export const ESG_PDF_HEADER = {
    pdfTableHeaderModernSlavery: [
        { text: 'Fund Name', style: 'tableHeader' },
        { text: 'Number of Companies with Red Flags', style: 'tableHeader' },
        { text: 'Percentage of Portfolio in Red Flag Companies (%)', style: 'tableHeader' }
    ],
    pdfTableHeaderDiversityInclusion: [
        { text: 'Fund Name', style: 'tableHeader' },
        { text: 'Boards with at least 30% Female Representation - Fund (%)', style: 'tableHeader' },
        { text: 'Boards with at least 30% Female Representation - Benchmark (%)', style: 'tableHeader' },
    ],
    pdfTableHeaderProxyVoting: [
        { text: 'Fund Name', style: 'tableHeader' },
        { text: 'Number of Meetings Voted - Australia', style: 'tableHeader' },
        { text: 'Percentage of Meetings Voted - Australia (%)', style: 'tableHeader' },
        { text: 'Number of Meetings Voted - Global ex-Australia', style: 'tableHeader' },
        { text: 'Percentage of Meetings Voted - Global ex-Australia (%)', style: 'tableHeader' },
        { text: 'Proportion of Votes Against Management Recommendations - Global (%)', style: 'tableHeader' },
        { text: 'Number of Supervotes - Global', style: 'tableHeader' },
    ],
    pdfTableHeaderACTCarbonWeight: [
        { text: 'Fund Name', style: 'tableHeader' },
        { text: 'Dark Grey', style: 'tableHeader' },
        { text: 'Light Grey', style: 'tableHeader' },
        { text: 'Grey/In-between', style: 'tableHeader' },
        { text: 'In-between', style: 'tableHeader' },
        { text: 'Green/In-between', style: 'tableHeader' },
        { text: 'Light Green', style: 'tableHeader' },
        { text: 'Dark Green', style: 'tableHeader' },
        { text: 'Not Assessed', style: 'tableHeader' }
    ],
    pdfTableHeaderACTCarbonIntensity: [
        { text: 'Fund Name', style: 'tableHeader' },
        { text: 'Dark Grey', style: 'tableHeader' },
        { text: 'Light Grey', style: 'tableHeader' },
        { text: 'Grey/In-between', style: 'tableHeader' },
        { text: 'In-between', style: 'tableHeader' },
        { text: 'Green/In-between', style: 'tableHeader' },
        { text: 'Light Green', style: 'tableHeader' },
        { text: 'Dark Green', style: 'tableHeader' }
    ],
    pdfTableHeaderDevGoalsLevel0: [
        { text: 'Fund Name', style: 'tableHeader' },
        { text: 'Overall (%)', style: 'tableHeader' }
    ],
    pdfTableHeaderDevGoalsLevel2: [
        { text: 'Fund Name', style: 'tableHeader' },
        { text: 'Social (%)', style: 'tableHeader' },
        { text: 'Environmental (%)', style: 'tableHeader' }
    ],
    pdfTableHeaderDevGoalsLevel3Social: [
        { text: 'Fund Name', style: 'tableHeader' },
        { text: 'Alleviating Poverty (%)', style: 'tableHeader' },
        { text: 'Attaining Gender Equality (%)', style: 'tableHeader' },
        { text: 'Combating Hunger (%)', style: 'tableHeader' },
        { text: 'Delivering Education (%)', style: 'tableHeader' },
        { text: 'Ensuring Health (%)', style: 'tableHeader' },
        { text: 'Providing Basic Services (%)', style: 'tableHeader' },
        { text: 'Safeguarding Peace (%)', style: 'tableHeader' },
    ],
    pdfTableHeaderDevGoalsLevel3Environmental: [
        { text: 'Fund Name', style: 'tableHeaderSI' },
        { text: 'Achieving Sustainable Agriculture and Forests (%)', style: 'tableHeaderSI' },
        { text: 'Conserving Water (%)', style: 'tableHeaderSI' },
        { text: 'Contributing to Sustainable Energy Use (%)', style: 'tableHeaderSI' },
        { text: 'Mitigating Climate Change (%)', style: 'tableHeaderSI' },
        { text: 'Optimising Material Use (%)', style: 'tableHeaderSI' },
        { text: 'Preserving Marine Ecosystems (%)', style: 'tableHeaderSI' },
        { text: 'Preserving Terrestrial Ecosystems (%)', style: 'tableHeaderSI' },
        { text: 'Promoting Sustainable Buildings (%)', style: 'tableHeaderSI' },
    ]
};

export enum EsgPdfLinks {
    MMCModernSlaveryStatement = 'https://www.mercer.com.au/content/dam/mercer/Aus/attachments/' +
    'au_2021-MMC_Modern-Slavery-Statement-2020_Final-Published.pdf',
    InvestmentApproachModernSlavery = 'https://www.mercer.com.au/content/dam/mercer/attachments/asia-pacific/' +
    'australia/investment/Mercer-Investment-Approach-to-Modern-Slavery_May-2021.pdf'
}
